import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import api from "../../axiosConfig";

const localDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const money = (value) => Number(value || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const userKey = (row) => String(row.user_id || "unassigned");

export default function RaportDevizeUser({ tipDeviz = "" }) {
  const [start, setStart] = useState(() => { const d = new Date(); d.setDate(1); return localDate(d); });
  const [end, setEnd] = useState(() => localDate(new Date()));
  const [selectedUser, setSelectedUser] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRows([]);
    setError("");
    if (!start || !end || start > end) {
      setError("Selectează o perioadă validă.");
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    api.get("/comenzi/interna/rapoarte/devize-user", {
      params: { start, end, ...(tipDeviz ? { tipcomanda_id: tipDeviz } : {}) },
    }).then(({ data }) => {
      if (!cancelled) setRows(data);
    }).catch(() => {
      if (!cancelled) setError("Raportul nu a putut fi încărcat. Încearcă din nou.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [start, end, tipDeviz, refresh]);

  const users = useMemo(() => Array.from(new Map(rows.map((row) => [userKey(row), row.user_name])).entries())
    .sort((a, b) => a[1].localeCompare(b[1], "ro")), [rows]);
  const groups = useMemo(() => {
    const grouped = new Map();
    rows.filter((row) => !selectedUser || userKey(row) === selectedUser).forEach((row) => {
      const key = userKey(row);
      if (!grouped.has(key)) grouped.set(key, { key, name: row.user_name, rows: [], piese: 0, manopera: 0, total: 0 });
      const group = grouped.get(key);
      group.rows.push(row);
      group.piese += Number(row.valoare_piese);
      group.manopera += Number(row.valoare_manopera);
      group.total += Number(row.valoare_total);
    });
    return [...grouped.values()].sort((a, b) => a.name.localeCompare(b.name, "ro"));
  }, [rows, selectedUser]);
  const totals = groups.reduce((total, group) => ({ count: total.count + group.rows.length, piese: total.piese + group.piese,
    manopera: total.manopera + group.manopera, total: total.total + group.total }), { count: 0, piese: 0, manopera: 0, total: 0 });

  return (
    <div className="bg-white p-3 rounded shadow-sm service-user-report">
      <h4>Devize / utilizator</h4>
      <p className="text-muted">Grupare după utilizatorul care a creat devizul. Valorile reprezintă piesele și manopera din deviz, fără TVA.</p>
      <Row className="g-3 mb-3 d-print-none align-items-end">
        <Col sm={3}><Form.Group controlId="devize-user-start"><Form.Label>De la</Form.Label><Form.Control type="date" value={start} onChange={(e) => setStart(e.target.value)} /></Form.Group></Col>
        <Col sm={3}><Form.Group controlId="devize-user-end"><Form.Label>Până la</Form.Label><Form.Control type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></Form.Group></Col>
        <Col sm={3}><Form.Group controlId="devize-user-filter"><Form.Label>Utilizator</Form.Label><Form.Select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
          <option value="">Toți utilizatorii</option>
          {selectedUser && !users.some(([id]) => id === selectedUser) && <option value={selectedUser}>Utilizator selectat (fără devize în perioada curentă)</option>}
          {users.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </Form.Select></Form.Group></Col>
        <Col sm={3} className="d-flex gap-2"><Button onClick={() => setRefresh((n) => n + 1)} disabled={loading}>Actualizează</Button><Button variant="outline-secondary" onClick={() => window.print()} disabled={loading || !!error || !groups.length}>Printează</Button></Col>
      </Row>
      <p>Perioada: {start} – {end} · {totals.count} devize · {groups.length} utilizatori</p>
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? <div role="status"><Spinner animation="border" size="sm" /> Se încarcă raportul…</div> : !error && <>
        {rows.some((row) => !row.user_id) && <p className="text-muted small">„Neatribuit” include devizele vechi pentru care utilizatorul creator nu a fost salvat.</p>}
        <Table responsive bordered hover size="sm">
          <thead><tr><th>Deviz</th><th>Data</th><th>Tip</th><th>Client</th><th>Auto / Șasiu</th><th>Piese</th><th>Manoperă</th><th>Total</th></tr></thead>
          <tbody>
            {!groups.length && <tr><td colSpan={8} className="text-center py-3">Nu există devize pentru filtrele selectate.</td></tr>}
            {groups.map((group) => <React.Fragment key={group.key}>
              <tr className="table-light"><th colSpan={8}>{group.name} — {group.rows.length} devize</th></tr>
              {group.rows.map((row) => <tr key={row.bxid}>
                <td><Link to={`/comenzi/interna/${row.bxid}/print`} target="_blank" rel="noreferrer">{row.bxnumar_comanda}</Link></td>
                <td>{String(row.bxdata_comanda).slice(0, 10)}</td>
                <td>{row.tipcomanda_id === 2 ? "Clienți" : row.tipcomanda_id === 1 ? "Intern" : "-"}</td>
                <td>{row.client || "-"}</td><td>{row.nr_auto || "-"}<br /><small>{row.bxsasiu}</small></td>
                <td className="text-end">{money(row.valoare_piese)}</td><td className="text-end">{money(row.valoare_manopera)}</td><td className="text-end">{money(row.valoare_total)}</td>
              </tr>)}
              <tr className="fw-bold"><td colSpan={5}>Total {group.name}</td><td className="text-end">{money(group.piese)}</td><td className="text-end">{money(group.manopera)}</td><td className="text-end">{money(group.total)}</td></tr>
            </React.Fragment>)}
          </tbody>
          <tfoot><tr className="fw-bold"><td colSpan={5}>Total general ({totals.count} devize)</td><td className="text-end">{money(totals.piese)}</td><td className="text-end">{money(totals.manopera)}</td><td className="text-end">{money(totals.total)}</td></tr></tfoot>
        </Table>
      </>}
      <style>{`@media print {
        .service-user-report { padding: 0 !important; box-shadow: none !important; }
        .service-user-report th, .service-user-report td { min-width: 0 !important; max-width: none !important; white-space: normal !important; }
        .service-user-report a { color: black; text-decoration: none; }
      }`}</style>
    </div>
  );
}
